import type { Chart, TooltipModel } from "chart.js";

const TOOLTIP_ATTRIBUTE = "data-external-chart-tooltip";
const CARD_INSET = 8;
const CARET_GAP = 8;

const createTooltipElement = (parent: HTMLElement): HTMLDivElement => {
  const current = parent.querySelector<HTMLDivElement>(
    `[${TOOLTIP_ATTRIBUTE}]`,
  );
  if (current) return current;

  const element = document.createElement("div");
  element.setAttribute(TOOLTIP_ATTRIBUTE, "true");
  element.setAttribute("aria-hidden", "true");
  Object.assign(element.style, {
    position: "absolute",
    zIndex: "10",
    maxWidth: "220px",
    padding: "8px 10px",
    borderRadius: "4px",
    background: "rgba(28, 31, 35, 0.94)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.22)",
    color: "#ffffff",
    fontFamily: "'Poppins', Inter, system-ui, sans-serif",
    fontSize: "12px",
    lineHeight: "1.35",
    opacity: "0",
    pointerEvents: "none",
    transition: "opacity 100ms ease",
    whiteSpace: "nowrap",
  });
  parent.appendChild(element);

  return element;
};

const appendTextLine = (
  container: HTMLElement,
  text: string,
  fontWeight?: string,
) => {
  const line = document.createElement("div");
  line.textContent = text;
  if (fontWeight) line.style.fontWeight = fontWeight;
  container.appendChild(line);
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), Math.max(min, max));

export const renderExternalDoughnutTooltip = ({
  chart,
  tooltip,
}: {
  chart: Chart;
  tooltip: TooltipModel<"doughnut">;
}) => {
  const parent = chart.canvas.parentElement;
  if (!parent) return;

  const element = createTooltipElement(parent);
  if (tooltip.opacity === 0) {
    element.style.opacity = "0";
    return;
  }

  element.replaceChildren();
  tooltip.title?.forEach((title) =>
    appendTextLine(element, title, "600"),
  );

  tooltip.body?.forEach((body, bodyIndex) => {
    body.lines.forEach((text) => {
      const row = document.createElement("div");
      row.style.display = "flex";
      row.style.alignItems = "center";
      row.style.gap = "4px";

      const color = tooltip.labelColors[bodyIndex]?.backgroundColor;
      if (typeof color === "string") {
        const swatch = document.createElement("span");
        Object.assign(swatch.style, {
          flex: "0 0 10px",
          width: "10px",
          height: "10px",
          borderRadius: "2px",
          background: color,
        });
        row.appendChild(swatch);
      }

      appendTextLine(row, text);
      element.appendChild(row);
    });
  });

  tooltip.footer?.forEach((footer) =>
    appendTextLine(element, footer, "600"),
  );

  element.style.opacity = "1";

  const parentBounds = parent.getBoundingClientRect();
  const cardBounds = chart.canvas.closest("article")?.getBoundingClientRect();
  const horizontalStart = cardBounds
    ? cardBounds.left - parentBounds.left + CARD_INSET
    : -element.offsetWidth;
  const horizontalEnd = cardBounds
    ? cardBounds.right - parentBounds.left - element.offsetWidth - CARD_INSET
    : parent.clientWidth;
  const preferredLeft = tooltip.caretX - element.offsetWidth - CARET_GAP;
  const left = clamp(preferredLeft, horizontalStart, horizontalEnd);

  const verticalStart = cardBounds
    ? cardBounds.top - parentBounds.top + CARD_INSET
    : 0;
  const verticalEnd = cardBounds
    ? cardBounds.bottom - parentBounds.top - element.offsetHeight - CARD_INSET
    : parent.clientHeight - element.offsetHeight;
  const preferredTop = tooltip.caretY - element.offsetHeight / 2;
  const top = clamp(preferredTop, verticalStart, verticalEnd);

  element.style.left = `${left}px`;
  element.style.top = `${top}px`;
};
